/**
 * BM-H469 — Terms of Business, for agreement, without the questionnaire.
 *
 * Reached by a client from the e-mail sent when their onboarding was
 * linked to another of their matters, or skipped. They are not asked for
 * their details again. They are asked to agree the retainer on THIS
 * matter, because the retainer is formed per matter and is never
 * inherited from another file.
 *
 * Deliberately the same acceptance the questionnaire takes: the same
 * terms, the same scroll gate, the same tick, a typed signature, and a
 * server that records the version, the hash of the text, the time, the
 * IP and the user agent.
 */
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { FIRM } from "../fixtures/firmIdentity";
// BM-H3522: the version comes from the same module as the text shown, so
// the two cannot drift apart again. The server records its own current
// version regardless of what is sent.
import { TOB_SECTIONS, TOB_VERSION } from "./onboarding/tobContent";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";

export default function TermsAgreement() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [tobAccepted, setTobAccepted] = useState(false);
  const [signature, setSignature] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [done, setDone] = useState(null);
  const tobContainerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/onboarding/terms/${token}`);
        if (!cancelled) setRecord(res.data);
      } catch (e) {
        if (!cancelled) {
          setLoadError(
            e?.response?.status === 404
              ? "This Terms of Business link is not recognised. Please contact us and we will send you a new one."
              : "We could not load the Terms of Business just now. Please try again shortly."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleScroll = () => {
    const el = tobContainerRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      setHasScrolledToBottom(true);
    }
  };

  const clientName = record?.client_name || "";
  const nameMatches =
    signature.trim().toLowerCase().replace(/[^a-z]/g, "") ===
    clientName.trim().toLowerCase().replace(/[^a-z]/g, "");
  const canSubmit =
    hasScrolledToBottom && tobAccepted && signature.trim() !== "" && !submitting;

  const submit = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await axios.post(`${API}/onboarding/terms/${token}/accept`, {
        full_name: clientName,
        signature: signature.trim(),
        agreed_tob: true,
        scroll_confirmed: hasScrolledToBottom,
        tob_version: TOB_VERSION,
      });
      setDone(res.data);
    } catch (e) {
      setSubmitError(
        errorMessage(e, "We could not record your agreement just now. Please try again, and if it keeps happening please contact us.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400"
        data-testid="terms-loading">
        Loading the Terms of Business
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="glass-card p-6 max-w-md text-center text-txt"
          data-testid="terms-load-error">
          {loadError}
        </div>
      </div>
    );
  }

  if (done || record?.already_accepted) {
    // A company's acceptance is only half the job. A director must still
    // guarantee the company's fees in their own name, and that signature
    // cannot be given by the same action. The existing guarantee step is
    // reused, not rebuilt here.
    // BM-H3522: read from the POST result or, after a reload, the GET
    // record, so a company client who comes back still sees the step.
    const g = done || record || {};
    const needsGuarantee = Boolean(g.guarantee_required && g.guarantee_link_token);
    const guaranteeRef = g.matter_reference || record?.matter_reference || "";
    const guaranteeHref = needsGuarantee
      ? `/client-onboarding?ref=${encodeURIComponent(guaranteeRef)}` +
        `&t=${encodeURIComponent(g.guarantee_link_token)}&step=guarantee`
      : "";
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="glass-card p-6 max-w-md text-center space-y-3"
          data-testid="terms-already-accepted">
          <h1 className="text-xl font-bold text-white">Thank you</h1>
          <p className="text-sm text-txt">
            Your agreement to our Terms of Business has been recorded for
            matter {record?.matter_reference}.{" "}
            {needsGuarantee
              ? "There is one more step: a director must guarantee the company's fees personally."
              : "There is nothing further for you to do. We will be in touch about your matter."}
          </p>
          {needsGuarantee && (
            <a
              href={guaranteeHref}
              className="inline-block w-full h-11 leading-[2.75rem] rounded-md bg-teal hover:bg-teal-hover text-white"
              data-testid="terms-continue-to-guarantee"
            >
              Continue to the director's guarantee
            </a>
          )}
          <p className="text-xs text-slate-400">
            {FIRM.name} | Terms of Business | {TOB_VERSION}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-3xl mx-auto" data-testid="terms-agreement-page">
        <div className="glass-card overflow-hidden">
          <div className="p-5 border-b border-navy-border">
            <h1 className="text-xl font-bold text-white">
              {FIRM.name}: Terms of Business
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {TOB_VERSION}, matter {record?.matter_reference}
            </p>
          </div>

          <div className="px-5 pt-4 text-sm text-txt">
            <p>
              We already act for you, so we have not asked you to complete our
              onboarding form again. Our terms apply to each matter separately,
              so we do need your agreement to them on this matter before we
              begin work.
            </p>
          </div>

          <div
            ref={tobContainerRef}
            onScroll={handleScroll}
            className="min-h-[420px] h-[60vh] overflow-y-auto p-5 space-y-6 text-sm text-txt leading-relaxed"
            data-testid="tob-scroll-container"
          >
            {TOB_SECTIONS.map((section, i) => (
              <div key={i}>
                <h3 className="font-semibold text-white mb-2">{section.title}</h3>
                <p className="whitespace-pre-line">{section.content}</p>
              </div>
            ))}
            <div className="pt-4 border-t border-navy-border">
              <p className="text-xs text-slate-400 text-center">
                {FIRM.name} | Terms of Business | {TOB_VERSION}
              </p>
            </div>
          </div>

          <div className="p-5 border-t border-navy-border bg-navy-hover/60 space-y-4">
            {!hasScrolledToBottom && (
              <p className="text-xs text-status-amber text-center"
                data-testid="scroll-prompt">
                Please scroll to the bottom of the Terms of Business to continue
              </p>
            )}

            <div className="flex items-start gap-2">
              <Checkbox
                id="tob-accept"
                checked={tobAccepted}
                onCheckedChange={(v) => setTobAccepted(Boolean(v))}
                disabled={!hasScrolledToBottom}
                data-testid="tob-checkbox"
              />
              <label
                htmlFor="tob-accept"
                className={`text-sm leading-snug ${
                  hasScrolledToBottom ? "text-txt" : "text-slate-500"
                }`}
              >
                I confirm that I have read and agree to {FIRM.name}'s Terms of
                Business ({TOB_VERSION}) for matter {record?.matter_reference}.
              </label>
            </div>

            <div className="space-y-1">
              <label className="text-sm text-txt" htmlFor="tob-signature">
                Please type your full name as your signature
              </label>
              <Input
                id="tob-signature"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                disabled={!hasScrolledToBottom}
                placeholder={clientName}
                data-testid="tob-signature-input"
              />
              {signature.trim() !== "" && !nameMatches && (
                <p className="text-xs text-status-amber" data-testid="signature-mismatch">
                  This should match the name we hold for you, {clientName}.
                </p>
              )}
            </div>

            {submitError && (
              <p className="text-sm text-status-red" data-testid="terms-submit-error">
                {String(submitError)}
              </p>
            )}

            <Button
              onClick={submit}
              disabled={!canSubmit}
              className="w-full h-11 bg-teal hover:bg-teal-hover text-white disabled:opacity-40"
              data-testid="terms-accept-button"
            >
              {submitting ? "Recording your agreement" : "Agree the Terms of Business"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
